<!--
  A general 'rich-text' editor component, built on top of
  [tiptap 1](https://github.com/ueberdosis/tiptap).

  You feed it html-content via its `content` prop. The user makes his edits,
  and you can then extract the changed content as html.

  Note: as of now the component comes pre configured with several 'rich-text'
  formatting features (e.g. 'bold', 'italic', etc). You can later enable more
  (see the code below) or add them as a prop option. This also goes for a
  toolbar: there is none at the moment, formatting is controlled via keyboard
  shortcuts, but you could enable props to control a toolbar later.

  The component supports two techniques, exemplified below, for accessing the
  changed content from the outside (i.e. from the parent component):

  a) by calling its native `getContentAsHtml()` method (using a template `ref`),
  b) by having the component pass the method to you (via an event).

  Technique b) is the way to go if you have a variable number of editor comps
  in the parent, because its hard to set up template refs (technique a) dynamically,
  at least in vue 2 composition api.

  A) Get content by calling the native `getContentAsHtml()` method.

  This involves setting up a named template-`ref` in the parent, binding it
  to the editor component, and then using the ref to access `getContentAsHtml()`:

  Example:
  ```ts
  <template>
    <editor
      ref="editorRef"
      :content="someText"
    />
  </template>

  <script lang="ts">
    // .. imports etc.

    export default defineComponent({

      components: { Editor },

      setup (props) {
        const editorRef = ref(null) // define a ref, that you bind to the editor compo
        const someText = '<p>hello!</p>'

        // This methods calls the native `getContentAsHtml` on the editor component
        const getEditorContent = () => {
          return (editorRef.value as unknown as & { getContentAsHtml: () => string })
          .getContentAsHtml()
        }

        // ... other stuff

        return {
          editorRef, // remember to make it accessible to the template
          someText,
        }
      }

    })
  </script>
  ```ts

  B) Get content by having the component pass the `getContentAsHtml()` method
  to you (via a `@set-content-callback` event) and the use that method as a
  callback whenever you want the current content.

  Example with several editors:
  ```ts
  <template>
    <template
      v-for="(field, name) in fields"
    >
      <editor
        :key="name"
        :content="content[name]"
        @set-content-callback="setEditorContentFn(name, $event)"
      />

    </template>
  </template>

  <script lang="ts">
    // .. imports etc.

    export default defineComponent({

      components: { Editor },

      setup (props) {
        const content = {
          someText: '<p>hello!</p>',
          otherText: '<p>hola!</p>',
        }

        // variable for storing the callbacks
        const editorCallBacks: { [key: string]: () => string } = {}

        // This methods 'stores' the callback method from each editor
        // it gets called on the `@set-content-callback` event which the
        // editor emits when mounted.
        const setEditorContentFn = (fieldName: string, cb: () => string) => {
          editorFieldsGetContentFns[fieldName] = cb
        }

        // You could then access the current content using the stored callbacks,
        // e.g. in method that loops over all the editor call backs:
        const getEditorContent = () => {
          const content: { [key: string]: string } = {}
          // eslint-disable-next-line no-restricted-syntax
          for (const [key, cb] of Object.entries(editorCallBacks)) {
            content[key] = cb()
          }
          return content
        }

        // ... other stuff

        return {
          content,
          setEditorContentFn,
        }
      }

    })
  </script>
  ```ts
-->

<template>
  <div class="editor">
    <!-- Examples of elements you can add with tiptap, keep for later: -->
    <!--
    <editor-menu-bar :editor="editor">

      <div class="menubar" slot-scope="{ commands, isActive }">

        <v-btn
          class="menubar__button"
          :class="{ 'is-active': isActive.bold() }"
          @click="commands.bold"
        >

        </v-btn>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.italic() }"
          @click="commands.italic"
        >

        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.strike() }"
          @click="commands.strike"
        >

        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.underline() }"
          @click="commands.underline"
        >

        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.code() }"
          @click="commands.code"
        >

        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.paragraph() }"
          @click="commands.paragraph"
        >

        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 1 }) }"
          @click="commands.heading({ level: 1 })"
        >
          H1
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 2 }) }"
          @click="commands.heading({ level: 2 })"
        >
          H2
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 3 }) }"
          @click="commands.heading({ level: 3 })"
        >
          H3
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.bullet_list() }"
          @click="commands.bullet_list"
        >

        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.ordered_list() }"
          @click="commands.ordered_list"
        >

        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.blockquote() }"
          @click="commands.blockquote"
        >

        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.code_block() }"
          @click="commands.code_block"
        >

        </button>

        <button
          class="menubar__button"
          @click="commands.horizontal_rule"
        >

        </button>

        <button
          class="menubar__button"
          @click="commands.undo"
        >

        </button>

        <button
          class="menubar__button"
          @click="commands.redo"
        >

        </button>

      </div>

    </editor-menu-bar>
-->
    <editor-content
      class="editor__content"
      :class="dynamicClass"
      :editor="editor"
    />
  </div>
</template>

<script>
// See https://github.com/scrumpy/tiptap
import { Editor, EditorContent /* EditorMenuBar */ } from 'tiptap'
import {
  BulletList,
  ListItem,
  Bold,
  Italic,
  Link,
  History,
  Placeholder,
  /*
Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  HorizontalRule,
  OrderedList,
  TodoItem,
  TodoList,

  Code,
  Strike,
  Underline,
  */
} from 'tiptap-extensions'

export default {
  name: 'Editor',
  components: {
    EditorContent,
    /* EditorMenuBar, */
  },
  props: {
    content: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editor: null,
      isEditable: true,
      isDisabled: false,
    }
  },
  computed: {

    dynamicClass() {
      let cssClass = ''
      if (this.outlined) {
        cssClass += ' outlined'
      }
      if (this.disabled) {
        cssClass += ' disabled'
      }
      return cssClass
    },
  },

  watch: {
    content(content) {
      this.editor.setContent(content)
    },
    isEditable() {
      const canEdit = this.isEditable && !this.isDisabled
      this.editor.setOptions({
        editable: canEdit,
      })
    },
    isDisabled() {
      if (this.isDisabled) {
        this.editor.setOptions({
          editable: false,
        })
      } else if (this.isEditable) {
        this.editor.setOptions({
          editable: true,
        })
      }
    },
    editable() {
      this.isEditable = this.editable
    },
    disabled() {
      this.isDisabled = this.disabled
    },
  },

  beforeDestroy() {
    this.editor.destroy()
  },
  mounted() {
    this.editor = new Editor({
      editable: true,
      extensions: [
        new BulletList(),
        new Italic(),
        new History(),
        new ListItem(),
        new Link(),
        new Bold(),
        new Placeholder({
          emptyEditorClass: 'is-editor-empty',
          emptyNodeClass: 'is-empty',
          emptyNodeText: '',
          showOnlyWhenEditable: true,
        }),
        /*
        new Blockquote(),
        new CodeBlock(),
        new HardBreak(),
        new Heading({ levels: [1, 2, 3] }),
        new HorizontalRule(),
        new OrderedList(),
        new TodoItem(),
        new TodoList(),
        new Code(),
        new Strike(),
        new Underline(),
        */
      ],
      content: this.content,
      onUpdate: () => {
        this.$emit('changed')
      },
      onFocus: () => {
        this.$emit('focus-change', true)
        this.$emit('focus')
      },
      onBlur: () => {
        this.$emit('focus-change', false)
        this.$emit('blur')
      },
      autoFocus: true,
    })
    if (this.placeholder !== '') {
      this.editor.extensions.options.placeholder.emptyNodeText = this.placeholder
    }
    this.emitGetContentCallback()
  },
  methods: {
    emitGetContentCallback() {
      this.$emit('set-content-callback', () => this.getContentAsHtml())
    },
    getContentAsHtml() {
      return this.editor.getHTML()
    },
  },
}
</script>

<style scoped>
.editor {
  font-size: 16px;
  color: black;
}

</style>

<style>

.editor p.is-editor-empty:first-child::before {
  content: attr(data-empty-text);
  float: left;
  color: #aaa;
  pointer-events: none;
  height: 0;
  font-style: italic;
}

/*
 * The purpose of these styles is to simulate vuetifys' behavior
 * for _outlined_ text boxes, e.g. the same border colors on hover,
 * focus, etc. They will take effect, if the components 'outline'
 * property = true.
 */
.editor__content.outlined {
  padding: 14px;
  border-radius: 4px;
  border-collapse: collapse;
  border-color: rgba(0, 0, 0, 0.38);
  border-style: solid;
  border-width: 1px;
  transition-duration: 0.3s;
  transition-property: border-color, border-width, box-shadow;
  transition-timing-function: cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Make border darker on hover  */
.editor__content.outlined:hover {
  border-color: rgba(0, 0, 0, 1);
}

/* Make border another color (blue) on focus  */
.editor__content.outlined:focus-within:not(.disabled) {
  border-color: rgb(25, 118, 210);
  /*
  The box-shadow below 'simulates' an extra outline/border
  ..it is used to make the border 'thicker', without taking
  up more space (without shifting the pixels of the text)
  */
  box-shadow: inset 0px 0px 0px 1px rgb(25, 118, 210);
}

div.ProseMirror.ProseMirror-focused {
  /** no user agent outline when focused */
  outline: none;
}

/* Grey text if disabled  */
.editor__content.disabled > div.ProseMirror {
  color: rgba(0, 0, 0, 0.38);
}

/* Slightly more faded border if disabled  */
.editor__content.outlined.disabled {
  border-color: rgba(0, 0, 0, 0.26);
}

</style>
